/**
 * useCreatorEarnings Hook
 * React state wrapper around CreatorEarningsApi.
 * Business logic lives in the API client for testability.
 * Shared between web and mobile.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  createCreatorEarningsApi,
  type EarningsData,
  type FollowerPreview,
  type PeriodDetail,
} from '../api/creatorEarningsApi';
import type { HttpClient } from '../api/httpClient';
import type { CreatorStatus } from '../creatorPayout';

// =============================================================================
// Types
// =============================================================================

export type { EarningsData, FollowerPreview, PeriodDetail } from '../api/creatorEarningsApi';

export interface UseCreatorEarningsOptions {
  httpClient: HttpClient;
  isAuthenticated: boolean;
}

export interface UseCreatorEarningsResult {
  // Creator status
  status: CreatorStatus | null;
  statusLoading: boolean;
  statusError: string | null;
  fetchStatus: () => Promise<void>;

  // Earnings history
  earnings: EarningsData | null;
  earningsLoading: boolean;
  earningsError: string | null;
  earningsHasMore: boolean;
  fetchEarnings: () => Promise<void>;
  loadMoreEarnings: () => Promise<void>;

  // Period detail (drill-down)
  periodDetail: PeriodDetail | null;
  periodDetailLoading: boolean;
  periodDetailError: string | null;
  fetchPeriodDetail: (periodLabel: string) => Promise<void>;
  clearPeriodDetail: () => void;

  // Live follower preview
  followerPreview: FollowerPreview | null;
  followerPreviewLoading: boolean;
  followerPreviewError: string | null;
  fetchFollowerPreview: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useCreatorEarnings(options: UseCreatorEarningsOptions): UseCreatorEarningsResult {
  const { httpClient, isAuthenticated } = options;

  const api = useMemo(() => createCreatorEarningsApi(httpClient), [httpClient]);

  // Status state
  const [status, setStatus] = useState<CreatorStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  // Earnings state
  const [earnings, setEarnings] = useState<EarningsData | null>(null);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [earningsError, setEarningsError] = useState<string | null>(null);
  const [earningsHasMore, setEarningsHasMore] = useState(false);
  const accumulatedPayoutsRef = useRef<EarningsData['payouts']>([]);

  // Period detail state
  const [periodDetail, setPeriodDetail] = useState<PeriodDetail | null>(null);
  const [periodDetailLoading, setPeriodDetailLoading] = useState(false);
  const [periodDetailError, setPeriodDetailError] = useState<string | null>(null);

  // Follower preview state
  const [followerPreview, setFollowerPreview] = useState<FollowerPreview | null>(null);
  const [followerPreviewLoading, setFollowerPreviewLoading] = useState(false);
  const [followerPreviewError, setFollowerPreviewError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated) return;

    setStatusLoading(true);
    setStatusError(null);

    try {
      const data = await api.getStatus();
      setStatus(data);
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'Failed to fetch creator status');
    } finally {
      setStatusLoading(false);
    }
  }, [api, isAuthenticated]);

  const PAGE_SIZE = 20;

  const fetchEarnings = useCallback(async () => {
    if (!isAuthenticated) return;

    setEarningsLoading(true);
    setEarningsError(null);
    accumulatedPayoutsRef.current = [];

    try {
      const data = await api.getEarnings(PAGE_SIZE, 0);
      accumulatedPayoutsRef.current = data.payouts;
      setEarnings(data);
      setEarningsHasMore(data.hasMore ?? false);
    } catch (err) {
      setEarningsError(err instanceof Error ? err.message : 'Failed to fetch earnings');
    } finally {
      setEarningsLoading(false);
    }
  }, [api, isAuthenticated]);

  const loadMoreEarnings = useCallback(async () => {
    if (!isAuthenticated || !earningsHasMore || earningsLoading) return;

    setEarningsLoading(true);
    setEarningsError(null);

    try {
      const data = await api.getEarnings(PAGE_SIZE, accumulatedPayoutsRef.current.length);
      accumulatedPayoutsRef.current = [...accumulatedPayoutsRef.current, ...data.payouts];
      setEarnings({
        ...data,
        payouts: accumulatedPayoutsRef.current,
      });
      setEarningsHasMore(data.hasMore ?? false);
    } catch (err) {
      setEarningsError(err instanceof Error ? err.message : 'Failed to fetch more earnings');
    } finally {
      setEarningsLoading(false);
    }
  }, [api, isAuthenticated, earningsHasMore, earningsLoading]);

  const fetchPeriodDetail = useCallback(
    async (periodLabel: string) => {
      if (!isAuthenticated) return;

      setPeriodDetailLoading(true);
      setPeriodDetailError(null);

      try {
        const data = await api.getPeriodDetail(periodLabel);
        setPeriodDetail(data);
      } catch (err) {
        setPeriodDetailError(err instanceof Error ? err.message : 'Failed to fetch period detail');
      } finally {
        setPeriodDetailLoading(false);
      }
    },
    [api, isAuthenticated],
  );

  const clearPeriodDetail = useCallback(() => {
    setPeriodDetail(null);
    setPeriodDetailError(null);
  }, []);

  const fetchFollowerPreview = useCallback(async () => {
    if (!isAuthenticated) return;

    setFollowerPreviewLoading(true);
    setFollowerPreviewError(null);

    try {
      const data = await api.getFollowerPreview();
      setFollowerPreview(data);
    } catch (err) {
      setFollowerPreviewError(
        err instanceof Error ? err.message : 'Failed to fetch follower preview',
      );
    } finally {
      setFollowerPreviewLoading(false);
    }
  }, [api, isAuthenticated]);

  return {
    status,
    statusLoading,
    statusError,
    fetchStatus,
    earnings,
    earningsLoading,
    earningsError,
    earningsHasMore,
    fetchEarnings,
    loadMoreEarnings,
    periodDetail,
    periodDetailLoading,
    periodDetailError,
    fetchPeriodDetail,
    clearPeriodDetail,
    followerPreview,
    followerPreviewLoading,
    followerPreviewError,
    fetchFollowerPreview,
  };
}
